import React, { FC, useCallback, useMemo } from "react"
import { Title, SimpleDropdown, CopyButton } from "dash-styled"
import { Breadcrumbs } from "../../../../Common/Breadcrumbs/Breadcrumbs"
import { JobStatusPill } from "../../Common/JobStatusPill"
import { TagsViewEdit } from "../../Common/TagsViewEdit"
import { routePaths } from "../../../../../../../Routes"
import { MultipleValueFilterOperator } from "../../../../../../../Models/Client/FilterExpression"
import { APIAppInstanceModel } from "../../../../../../../Models/API/AppInstanceModel"
import {
    APIJobModel,
    APIJobQueryModel,
    JobVersionItem
} from "../../../../../../../Models/API/JobModel"
import { SortExpression } from "../../../../../../../Models/Client/SortExpression"

interface DetailViewHeaderProps {
    orgUsername: string
    job: APIJobModel
    instance: APIAppInstanceModel
    sort: SortExpression<APIJobQueryModel>
    versionsList: JobVersionItem[]
    selectedVersion: number
    showOldVersion: boolean
    onAddTag?: (value: string, isNew: boolean) => Promise<void>
    onRemoveTag?: (value: string) => Promise<void>
    onChangeSelectedVersion?: (version: number) => void
    showBreadcrumbs?: boolean
}

export const DetailViewHeader: FC<DetailViewHeaderProps> = ({
    orgUsername,
    job,
    instance,
    sort,
    versionsList,
    selectedVersion,
    showOldVersion,
    onAddTag,
    onRemoveTag,
    onChangeSelectedVersion,
    showBreadcrumbs
}) => {
    const tagFilterLinkFactory = useCallback(
        (tag: string) =>
            routePaths.appDetail(orgUsername, job.appId, {
                filter: [
                    {
                        fieldName: "tags",
                        operator: MultipleValueFilterOperator.isOneOf,
                        value: [tag]
                    }
                ],
                sort
            }),
        [job.appId, orgUsername, sort]
    )

    const version = useMemo<JobVersionItem | undefined>(
        () => versionsList[selectedVersion],
        [selectedVersion, versionsList]
    )

    const versionDropdownTitle = useMemo(
        () =>
            `${showOldVersion ? version?.versionName : "Latest version"} (of ${
                versionsList.length
            })`,
        [showOldVersion, version, versionsList.length]
    )

    return (
        <div data-testid="detail-view-header">
            {showBreadcrumbs && (
                <Breadcrumbs
                    elements={["Projects", instance.name, "Work Queue"]}
                />
            )}
            <div className="flex-shrink-0 flex items-center justify-between pb-2">
                <Title
                    component="h3"
                    variant="heading5"
                    weight="semibold"
                    className="flex-shrink flex-grow-0"
                    noMargin={true}
                    testId="job-id"
                >
                    Data point {job.id}
                    <CopyButton
                        className="ml-2"
                        variant="simple"
                        value={window.location.href}
                    />
                </Title>
                <TagsViewEdit
                    className="ml-4 justify-end w-0 flex-shrink flex-grow min-w-32"
                    rightAlign={true}
                    appID={instance.uuid}
                    value={job.tags ?? []}
                    linkFactory={tagFilterLinkFactory}
                    onAdd={onAddTag}
                    onRemove={onRemoveTag}
                />
                {version !== undefined ? (
                    <JobStatusPill
                        job={{
                            status: version?.status,
                            correct: version?.correct,
                            jobType: job.jobType
                        }}
                        className="ml-1 flex-shrink-0"
                    />
                ) : null}
            </div>
            <div className="flex justify-start flex-shrink-0">
                {versionsList.length > 1 ? (
                    <SimpleDropdown
                        testId="version-dropdown"
                        items={versionsList}
                        title={versionDropdownTitle}
                        onChange={(version: number) => {
                            onChangeSelectedVersion?.(version)
                        }}
                    />
                ) : null}
            </div>
        </div>
    )
}
